import { Outlet } from '@buzola/router'
import { ContemberClient } from '@contember/react-client'
import { IdentityProvider, IdentityState } from '@contember/react-identity'
import { useEffect } from 'react'
import { AdminLayout } from '~/app/components/admin-layout'
import { GdSubscriptionProvider } from '~/app/components/gd-subscription'
import { PageSkeleton } from '~/app/components/skeleton'
import { UnsavedChangesGuard } from '~/app/components/unsaved-changes-guard'
import { ContemberProvider } from '~/app/contember-provider'
import { adminConfig } from '~/entrypoint/config'
import { LoadingScreen } from '~/entrypoint/login-screen'

function RedirectToLogin() {
	useEffect(() => {
		window.location.href = adminConfig.loginUrl
	}, [])
	return <LoadingScreen />
}

function AppShell() {
	return (
		<ContemberProvider>
			<UnsavedChangesGuard />
			{/* Nečinný, dokud si o IČO neřekne buňka „GD předplatné" — viz `components/gd-subscription.tsx`. */}
			<GdSubscriptionProvider>
				<AdminLayout>
					{/* Route modules are lazy chunks; without a fallback the content area goes blank on every navigation. */}
					<Outlet fallback={<PageSkeleton />} />
				</AdminLayout>
			</GdSubscriptionProvider>
		</ContemberProvider>
	)
}

// Contember's default tenant emails link to `<host>/?page=passwordReset&token=…`.
// Translate that to our buzola route so the link works once the project's
// admin URL points at this host.
function consumeContemberPageParam(): boolean {
	if (typeof window === 'undefined') return false
	const sp = new URLSearchParams(window.location.search)
	const page = sp.get('page')
	const token = sp.get('token')
	if (page === 'passwordReset') {
		const qs = token ? `?token=${encodeURIComponent(token)}` : ''
		window.location.replace(`${adminConfig.loginUrl}/password-reset${qs}`)
		return true
	}
	if (page === 'resetRequest' || page === 'passwordResetRequest') {
		window.location.replace(`${adminConfig.loginUrl}/reset-request`)
		return true
	}
	return false
}

export default function RootLayout() {
	const pathname = typeof window !== 'undefined' ? window.location.pathname : '/'

	if (consumeContemberPageParam()) {
		return <LoadingScreen />
	}

	// `/login` subtree provides its own ContemberClient (login token).
	if (pathname === adminConfig.loginUrl || pathname.startsWith(`${adminConfig.loginUrl}/`)) {
		return (
			<div className="min-h-screen">
				<Outlet fallback={<LoadingScreen />} />
			</div>
		)
	}

	// Dev: local root session token works directly — no login gate.
	if (!adminConfig.requireLogin) {
		return (
			<div className="min-h-screen">
				<ContemberClient
					apiBaseUrl={adminConfig.apiBaseUrl}
					sessionToken={adminConfig.sessionToken}
					project={adminConfig.projectName}
					stage={adminConfig.stage}
				>
					<AppShell />
				</ContemberClient>
			</div>
		)
	}

	// Prod: session token is a placeholder swapped by the worker proxy from the
	// CONTEMBER_TOKEN cookie. No cookie / invalid → IdentityState 'none' → login.
	return (
		<div className="min-h-screen">
			<ContemberClient
				apiBaseUrl={adminConfig.apiBaseUrl}
				sessionToken={adminConfig.sessionToken}
				project={adminConfig.projectName}
				stage={adminConfig.stage}
			>
				<IdentityProvider>
					<IdentityState state="success">
						<AppShell />
					</IdentityState>
					<IdentityState state={['none', 'cleared', 'failed']}>
						<RedirectToLogin />
					</IdentityState>
					<IdentityState state="loading">
						<LoadingScreen />
					</IdentityState>
				</IdentityProvider>
			</ContemberClient>
		</div>
	)
}
